import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Space,
  message, Popconfirm, Card, Row, Col,
  Pagination, Alert, Tag, Badge
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined,
  SearchOutlined, SafetyCertificateOutlined
} from '@ant-design/icons';
import axios from '../../utils/axios';

const { Option } = Select;
const { TextArea } = Input;

const PermissionManagementPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
 
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPermissions, setTotalPermissions] = useState(0);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Fetch permissions
  const fetchPermissions = async (page = 1, size = 10) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/permissions', {
        params: {
          page,
          limit: size,
          search: searchText
        }
      });
     
      if (response.data.success) {
        const permissionsData = response.data.permissions || [];
        const formattedPermissions = permissionsData.map(permission => ({
          ...permission,
          formattedCreatedAt: formatDate(permission.createdAt),
          formattedUpdatedAt: formatDate(permission.updatedAt)
        }));
        setPermissions(formattedPermissions);
        setTotalPermissions(response.data.total || permissionsData.length);
        setCurrentPage(page);
        setPageSize(size);
      } else {
        message.error(response.data.message || 'Error loading permissions');
        setPermissions([]);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      message.error('Error loading permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions(currentPage, pageSize);
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setErrorMessage('');
   
    try {
      if (editingPermission) {
        values.id = editingPermission._id;
        const response = await axios.put(`/api/permissions/${editingPermission._id}`, values);
       
        if (response.data.success) {
          message.success('Permission updated successfully');
          setModalVisible(false);
          setEditingPermission(null);
          form.resetFields();
          fetchPermissions(currentPage, pageSize);
        } else {
          setErrorMessage(response.data.message || 'Error updating permission');
        }
      } else {
        const response = await axios.post('/api/permissions', values);
       
        if (response.data.success) {
          message.success('Permission added successfully');
          setModalVisible(false);
          form.resetFields();
          fetchPermissions(currentPage, pageSize);
        } else {
          setErrorMessage(response.data.message || 'Error creating permission');
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(error.response?.data?.message || 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (permission) => {
    try {
      const response = await axios.delete(`/api/permissions/${permission._id}`);
      if (response.data.success) {
        message.success('Permission deleted successfully');
        fetchPermissions(currentPage, pageSize);
      } else {
        message.error(response.data.message || 'Error deleting permission');
      }
    } catch (error) {
      console.error('Error deleting permission:', error);
      message.error(error.response?.data?.message || 'Error deleting permission');
    }
  };

  const handleSearch = () => {
    fetchPermissions(1, pageSize);
  };

  const handlePageChange = (page, size) => {
    fetchPermissions(page, size);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: '_id',
      key: 'id',
      width: 50,
      render: (text, record, index) => {
        const pageIndex = (currentPage - 1) * pageSize + index + 1;
        return pageIndex;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SafetyCertificateOutlined style={{ color: '#1890ff', marginRight: 8 }} />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        let color = 'blue';
        if (category === 'admin') color = 'red';
        else if (category === 'user') color = 'green';
        else if (category === 'read') color = 'orange';
        else if (category === 'write') color = 'purple';
        
        return (
          <Tag color={color}>
            {category}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Badge 
          status={isActive ? 'success' : 'error'} 
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingPermission(record);
              setErrorMessage('');
              form.setFieldsValue({
                name: record.name,
                description: record.description,
                category: record.category || 'general',
                isActive: record.isActive
              });
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Are you sure to delete this permission?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Data for demo
  const demoData = [
    {
      _id: '1',
      name: 'users.manage',
      description: 'Manage user accounts',
      category: 'admin',
      isActive: true,
      formattedCreatedAt: '2023-06-18 00:55:39',
      formattedUpdatedAt: '2023-06-23 12:13:07'
    },
    {
      _id: '2',
      name: 'roles.view',
      description: 'View roles list',
      category: 'read',
      isActive: true,
      formattedCreatedAt: '2023-05-15 10:30:25',
      formattedUpdatedAt: '2023-06-20 14:45:10'
    },
    {
      _id: '3',
      name: 'dashboard.view',
      description: 'View dashboard',
      category: 'user',
      isActive: true,
      formattedCreatedAt: '2023-04-10 08:15:30',
      formattedUpdatedAt: '2023-06-18 09:20:15'
    }
  ];

  // Use demo data if no real data
  const displayPermissions = permissions.length > 0 ? permissions : demoData;
  const displayTotal = permissions.length > 0 ? totalPermissions : demoData.length;

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#ffffff',
      minHeight: '100vh'
    }}>
      <Card style={{ borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', border: '1px solid #5fa5e7'}}>
        {/* Header avec le bouton Add New Permission en haut à droite */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '16px',
            borderRadius: '8px'

          }}>
            <div>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '600',
                color: '#333'
              }}>
                Gestion des Permissions
              </h1>
            </div>
            
            {/* Bouton Add New Permission en haut à droite */}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPermission(null);
                setErrorMessage('');
                form.resetFields();
                setModalVisible(true);
              }}
              style={{
                backgroundColor: '#5fa5e7',
                borderColor: '#1890ff',
                padding: '8px 20px',
                height: 'auto',
                marginTop: '8px'
              }}
            >
              Ajouter Permission
            </Button>
          </div>
        </div>

        {/* Entries info */}
        <div style={{
          marginBottom: '16px',
          padding: '8px 16px',
          backgroundColor: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: '4px',
          fontSize: '14px',
          color: '#666'
        }}>
          Show {pageSize} entries
        </div>

        {/* Search bar */}
        <div style={{ marginBottom: '20px' }}>
          <Row gutter={16} align="middle">
            <Col>
              <Input
                placeholder="Search for..."
                style={{ width: '200px' }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{ backgroundColor: '#5fa5e7', borderColor: '#1890ff' }}
              >
                Recherche avancée
              </Button>
            </Col>
          </Row>
        </div>

        {/* Table Card */}
        <Card
          bordered={true}
          style={{
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            marginBottom: '20px',
            padding: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '2px solid #dee2e6'
                }}>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>ID</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Name</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Description</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Category</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057',
                    borderRight: '1px solid #dee2e6'
                  }}>Status</th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: '600',
                    color: '#495057'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayPermissions.map((permission, index) => (
                  <tr key={permission._id} style={{
                    borderBottom: '1px solid #dee2e6',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                  }}>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>{index + 1}</td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057',
                      fontWeight: '500'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <SafetyCertificateOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                        {permission.name}
                      </div>
                    </td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>{permission.description}</td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>
                      <span style={{
                        backgroundColor: permission.category === 'admin' ? '#fff1f0' :
                                        permission.category === 'user' ? '#f6ffed' :
                                        permission.category === 'read' ? '#fff7e6' :
                                        permission.category === 'write' ? '#f9f0ff' : '#e6f7ff',
                        color: permission.category === 'admin' ? '#cf1322' :
                              permission.category === 'user' ? '#227100' :
                              permission.category === 'read' ? '#d46b08' :
                              permission.category === 'write' ? '#722ed1' : '#0084ff',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {permission.category}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px',
                      borderRight: '1px solid #dee2e6',
                      color: '#495057'
                    }}>
                      <span style={{
                        color: permission.isActive ? '#227100' : '#ff4d4f',
                        fontWeight: '500'
                      }}>
                        {permission.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px',
                      color: '#495057'
                    }}>
                      <Space>
                        <Button
                          type="primary"
                          onClick={() => {
                            setEditingPermission(permission);
                            setErrorMessage('');
                            form.setFieldsValue({
                              name: permission.name,
                              description: permission.description,
                              category: permission.category || 'general',
                              isActive: permission.isActive
                            });
                            setModalVisible(true);
                          }}
                          style={{
                            backgroundColor: '#049472',
                            borderColor: '#236153',
                            fontSize: '12px',
                            padding: '4px 4px',
                            height: 'auto',
                            borderRadius: '50%'
                          }}
                        >
                            <EditOutlined />
                        </Button>
                        <Popconfirm
                          title="Are you sure to delete this permission?"
                          onConfirm={() => handleDelete(permission)}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{
                            style: {
                              backgroundColor: '#dc3545',
                              borderColor: '#dc3545'
                            }
                          }}
                        >
                          <Button
                            danger
                            style={{
                              backgroundColor: '#dc3545',
                              borderColor: '#dc3545',
                              color: 'white',
                              fontSize: '12px',
                              padding: '4px 4px',
                              height: 'auto',
                              borderRadius: '50%'
                            }}
                          >
                            < DeleteOutlined />
                          </Button>
                        </Popconfirm>
                      </Space>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pagination info and controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '12px 16px',
          backgroundColor: '#fafafa',
          border: '1px solid #e8e8e8',
          borderRadius: '4px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, displayTotal)} to {Math.min(currentPage * pageSize, displayTotal)} of {displayTotal} entries
          </div>
         
          <div>
            <Space>
              <Button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1, pageSize)}
                style={{ color: currentPage === 1 ? '#ccc' : '#1890ff' }}
              >
                Previous
              </Button>
             
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={displayTotal}
                onChange={handlePageChange}
                showSizeChanger={false}
                size="small"
                itemRender={(page, type, originalElement) => {
                  if (type === 'page') {
                    return (
                      <Button
                        type={page === currentPage ? 'primary' : 'default'}
                        size="small"
                        style={{
                          minWidth: '32px',
                          backgroundColor: page === currentPage ? '#1890ff' : 'transparent',
                          borderColor: page === currentPage ? '#1890ff' : '#d9d9d9'
                        }}
                      >
                        {page}
                      </Button>
                    );
                  }
                  return originalElement;
                }}
              />
             
              <Button
                disabled={currentPage * pageSize >= displayTotal}
                onClick={() => handlePageChange(currentPage + 1, pageSize)}
                style={{ color: currentPage * pageSize >= displayTotal ? '#ccc' : '#1890ff' }}
              >
                Next
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Modal pour ajouter/modifier une permission */}
      <Modal
        title={editingPermission ? 'Edit Permission' : 'Add New Permission'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingPermission(null);
          form.resetFields();
          setErrorMessage('');
        }}
        footer={null}
        width={500}
        style={{ top: 20 }}
        bodyStyle={{ padding: '0' }}
      >
        {errorMessage && (
          <Alert
            message="Error"
            description={errorMessage}
            type="error"
            showIcon
            style={{
              margin: '20px 20px 0 20px',
              borderRadius: '4px'
            }}
            closable
            onClose={() => setErrorMessage('')}
          />
        )}
       
        <div style={{ padding: '20px' }}>
          <h1 style={{
            margin: '0 0 16px 0',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
            borderBottom: '1px solid #eee',
            paddingBottom: '10px'
          }}>
            {editingPermission ? 'Edit Permission' : 'Add New Permission'}
          </h1>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: '16px' }}
          >
            <div style={{ marginBottom: '24px' }}>
              {!editingPermission && (
                <Form.Item
                  name="name"
                  label={
                    <span style={{
                      fontWeight: 'bold',
                      color: '#333',
                      fontSize: '14px'
                    }}>
                      Permission Name *
                    </span>
                  }
                  rules={[{ required: true, message: 'Please enter the permission name' }]}
                >
                  <Input
                    placeholder="e.g., users.manage, roles.view"
                    style={{
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      padding: '8px 12px'
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="description"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Description *
                  </span>
                }
                rules={[{ required: true, message: 'Please enter the description' }]}
              >
                <TextArea
                  placeholder="Permission description"
                  rows={3}
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    padding: '8px 12px'
                  }}
                />
              </Form.Item>
            </div>

            <div style={{
              borderTop: '1px solid #dee2e6',
              margin: '24px 0',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'white',
                padding: '0 10px',
                color: '#6c757d',
                fontSize: '12px'
              }}>
                ● ● ●
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <Form.Item
                name="category"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Category *
                  </span>
                }
                rules={[{ required: true, message: 'Please select a category' }]}
                initialValue="general"
              >
                <Select
                  placeholder="Select category"
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ced4da'
                  }}
                >
                  <Option value="admin">Admin</Option>
                  <Option value="user">User</Option>
                  <Option value="read">Read</Option>
                  <Option value="write">Write</Option>
                  <Option value="general">General</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="isActive"
                label={
                  <span style={{
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '14px'
                  }}>
                    Status *
                  </span>
                }
                rules={[{ required: true, message: 'Please select status' }]}
                initialValue={true}
              >
                <Select
                  placeholder="Select status"
                  style={{
                    borderRadius: '4px',
                    border: '1px solid #ced4da'
                  }}
                >
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '20px',
              borderTop: '1px solid #eee'
            }}>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  setEditingPermission(null);
                  form.resetFields();
                  setErrorMessage('');
                }}
                disabled={submitting}
                style={{
                  border: '1px solid #6c757d',
                  color: '#6c757d',
                  borderRadius: '4px',
                  padding: '8px 24px',
                  fontWeight: '500',
                  backgroundColor: 'transparent',
                  minWidth: '100px'
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  borderRadius: '4px',
                  padding: '8px 24px',
                  fontWeight: '500',
                  minWidth: '100px'
                }}
              >
                {editingPermission ? 'Update' : 'Validate'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      <style jsx>{`
        :global(.ant-table-thead > tr > th) {
          background-color: #fafafa;
          font-weight: 600;
          color: #333;
          border-bottom: 2px solid #e8e8e8;
        }
       
        :global(.ant-table-tbody > tr > td) {
          border-bottom: 1px solid #e8e8e8;
        }
       
        :global(.ant-table-tbody > tr:hover > td) {
          background-color: #f5f5f5;
        }
       
        :global(.ant-card) {
          border: 1px solid #e8e8e8;
        }
       
        :global(.ant-btn-primary) {
          background-color: #1890ff;
          border-color: #1890ff;
        }
       
        :global(.ant-btn-primary:hover) {
          background-color: #40a9ff;
          border-color: #40a9ff;
        }
      `}</style>
    </div>
  );
};

export default PermissionManagementPage;